import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BreadcrumbContext } from '../../Layout/Layout';
import TableConfig from './sitesTableConfig.json';
import { useDispatch, useSelector } from 'react-redux';
import { sitesAdded } from '../sitesSlice';
import { deleteSite, viewEditSite } from './sitesUtilities';
import { getSitesData } from '../sitesAPI';
import { getCustomersDropdownData } from '../../dataAPI';
import { customersDropdownDataAdded } from '../../../appSlices';
import Table from '../../../atoms/Table/Table';
import SelectDropdown from '../../../atoms/SelectDropdown';
import { getUserData } from '../../../utilities/app';

function AllCustomers() {
  const { setCurrentBreadcrumb } = useContext(BreadcrumbContext);
  const { isCustomerUser, customerID } = getUserData();
  const navigate = useNavigate();
  const { header, customerHeader, exportableColumns, visibleColumns, customerExportableColumns, customerVisibleColumns } = TableConfig;
  const dispatch = useDispatch();
  const sites = useSelector(state => state.site.sites);
  const updateSites = useSelector(state => state.site.updateSites);
  const customersDropdownData = useSelector(state => state.appData.customers);
  const [originalGridData, setOriginalSitesData] = useState([]);
  const [contactNameData, setContactNameData] = useState([]);
  const [gridData, setSitesData] = useState(sites);
  const [customers, setCustomers] = useState(customersDropdownData);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    isCustomerUser && !customerID && navigate('/dashboard');
  }, [customerID]);

  const getContactName = (sites) => {
    let contacts = [];
    sites.forEach(site => {
      !contacts.includes(site.sitE_CONTACT) && contacts.push(site.sitE_CONTACT);
    });
    setContactNameData([...contacts]);
  }

  useEffect(() => {
    setCurrentBreadcrumb([
      {
        "linkText": isCustomerUser ? "My Summary" : "Customers",
        "path": isCustomerUser ? "/customers/view" : "/customers"
      },
      {
        "linkText": isCustomerUser ? "All Sites" : "Sites",
        "path": "/sites"
      }
    ]);
    (!customersDropdownData || !customersDropdownData.length) && getCustomersDropdownData(data => dispatch(customersDropdownDataAdded(data)));
    return () => {}
  }, []);

  useEffect(() => {
    updateSites && getSitesData(data => dispatch(sitesAdded(data)), customerID);
  }, [updateSites]);

  useEffect(() => {
    setLoadingData(true);
    getSitesData(data => {
        dispatch(sitesAdded(data));
        setLoadingData(false);
      },
      customerID
    );
  }, []);

  useEffect(() => {
    setCustomers(customersDropdownData);
  }, [customersDropdownData]);

  const getCustomerID = (customeR_ID) => customers?.find(customer => customer.customeR_ID === customeR_ID)?.cusT_ID;

  const parseSitesData = data => data.map(row => ({
    ...row,
    cusT_ID: getCustomerID(row.customeR_ID)
  }))

  useEffect(() => {
    const data = parseSitesData(sites);
    setSitesData(data);
    setOriginalSitesData(data);
    getContactName(data);
  }, [sites]);

  const handleAddAction = () => { navigate('/sites/add'); };

  const clearSelectedFilter = () => {
    setSelectedFilters({
      sitE_ID: '',
      customeR_ID: '',
      sitE_CONTACT: ''
    });
    setSitesData([...originalGridData]);
  }

  const filterCustomerData = (filterByAttr, selectedValue) => {
    setSelectedFilters({
      [filterByAttr]: selectedValue
    });

    if(!selectedValue) return setSitesData(originalGridData);

    const searchCustomersData = [];
    originalGridData.forEach((row) => {
      row[filterByAttr]?.toString()?.includes(selectedValue) && searchCustomersData.push(row);
    });
    setSitesData(searchCustomersData);
  };

  const generateDataColumn = (key, value, row) => {
    if(key === "actions") return (
      <div className="sites-grid-row-actions">
        <button onClick={() => viewEditSite('view', row, navigate)}>
          <img src="/assets/customers/view.png"/>
        </button>
        <button onClick={() => viewEditSite('edit', row, navigate)}>
          <img src="/assets/customers/edit.png"/>
        </button>
        <button onClick={() => deleteSite(row, () => getSitesData(data => dispatch(sitesAdded(data)), customerID))}>
          <img src="/assets/customers/delete.png"/>
        </button>
      </div>
    );

    return value;
  }

  const renderTableFilters = () => (
    <div className="sites-filter">
      <div className="filter-group">
        <label>Filter by</label>
        <SelectDropdown
          name="siteFilter"
          value={selectedFilters.sitE_ID}
          onChange={(selectedOption) => filterCustomerData('sitE_ID', selectedOption?.value || '')}
          placeholder="Site Name"
          options={originalGridData.map((site) => ({
            value: site.sitE_ID,
            label: site.sitE_NAME
          }))}
          isClearable
        />
      </div>
      { !isCustomerUser ? (
        <div className="filter-group">
          <label>Filter by</label>
          <SelectDropdown
            name="customerFilter"
            value={selectedFilters.customeR_ID}
            onChange={(selectedOption) => filterCustomerData('customeR_ID', selectedOption?.value || '')}
            placeholder="Customer ID"
            options={customers.map((customer) => ({
              value: customer.customeR_ID,
              label: customer.cusT_ID
            }))}
            isClearable
          />
        </div>
      ) : <></> }
      <div className="filter-group">
        <label>Filter by</label>
        <SelectDropdown
          name="contactFilter"
          value={selectedFilters.sitE_CONTACT}
          onChange={(selectedOption) => filterCustomerData('sitE_CONTACT', selectedOption?.value || '')}
          placeholder="Contact Name"
          options={contactNameData.map((name) => ({
            value: name,
            label: name
          }))}
          isClearable
        />
      </div>
      <button className="sites-filter-clear" onClick={clearSelectedFilter}>Clear Selected</button>
    </div>
  );

  return (
    <div className="sites">
      <Table
        renderTableFilters={renderTableFilters}
        handleAddAction={handleAddAction}
        TableTitle={`Sites`}
        header={isCustomerUser ? customerHeader : header}
        visibleColumns={isCustomerUser ? customerVisibleColumns : visibleColumns}
        exportableColumns={isCustomerUser ? customerExportableColumns : exportableColumns}
        originalData={originalGridData}
        data={gridData}
        rowId={'sitE_ID'}
        generateDataColumn={generateDataColumn}
        loadingData={loadingData}
      />
    </div>
  );
};

export default AllCustomers;
